import React, { useEffect, useMemo, useState } from "react";
import { Box, Center, Checkbox, Flex, FormControl, FormLabel, HStack, Spinner, Stack, Text } from "@chakra-ui/react";
import { CardCvcElement, CardExpiryElement, CardNumberElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation, useQuery } from "@apollo/client";
import { SMILE_PLUS_SUBSCRIBE } from "src/graphql/Mutations/Plan";
import { SmilePlusSubscribePayload, SmilePlusSubscribeVariables } from "types/Mutations/Plan";
import { SmilePlusSubscriptionPlanProductEnum } from "types/User";
import { useStripeToast } from "src/shared/hooks/useStripeToast";
import useTranslation from "next-translate/useTranslation";
import { useAuthDispatch, useAuthState } from "src/shared/contexts/AuthContext";
import { PixMidTitle } from "src/shared/components/Titles";
import { Button } from "src/shared/components/Button";
import * as GTagHelper from "src/utils/helpers/GTagHelper";
import { calculateSmilePlusPrice, findSmilePlusPromoDetails, isSmilePlusValidPlanPromo } from "src/utils/promoCodes";
import { track } from "src/services/Segment/analytics";
import { useSentry } from "src/services/Sentry";
import SmileSharedPlanRadioGroup from "src/modules/smile/shared/components/Forms/SmileSharedPlanRadioGroup";
import { SMILE_PLUS_PLANS } from "src/graphql/Queries/Plan";
import { SmilePlusPlansPayload } from "src/shared/types/Queries/Plan";
import { APP_SIGNUP_SUCCESS_ROUTE } from "src/modules/smile/shared/consts";
import { Storage } from "src/services/Storage";
import { IS_HYBRIDGE } from "src/modules/smile/shared/consts";
import { FormInput } from "src/shared/components/FormInput";
import { createFormatPrice } from "src/utils/formatPrice";

interface FormData {
  plan: SmilePlusSubscriptionPlanProductEnum;
  newsletter: boolean;
  terms: boolean;
  coupon: string;
}

const SmileSignupSubscriptionForm = () => {
  const { lang, t } = useTranslation();
  const { profile } = useAuthState();
  const { updateUserInfos, setHasJustRegistered } = useAuthDispatch();
  const { log } = useSentry();
  const stripe = useStripe();
  const elements = useElements();
  const { sendSuccessToast, sendStripeErrorToast } = useStripeToast();
  const [cardReady, setCardReady] = useState({ number: false, expiration: false, cvc: false });
  const { data: plans } = useQuery<SmilePlusPlansPayload>(SMILE_PLUS_PLANS);

  const [smilePlusSubscribeMutation] = useMutation<SmilePlusSubscribePayload, SmilePlusSubscribeVariables>(
    SMILE_PLUS_SUBSCRIBE,
    {
      onError: (error) => {
        sendStripeErrorToast(error.message);
      },
    }
  );

  const defaultPlan = Storage.smileSelectedPlan.getOr(SmilePlusSubscriptionPlanProductEnum.SMILE_PLUS_MONTHLY);

  const formMethods = useForm<FormData>({
    mode: "onBlur",
    defaultValues: {
      plan: defaultPlan,
      newsletter: false,
      terms: false,
      coupon: "",
    },
  });

  useEffect(() => {
    reset({ plan: defaultPlan, newsletter: false, terms: false, coupon: "" });
  }, [defaultPlan]);

  const currency = plans?.smilePlusPlans.currency;

  const formatPrice = useMemo(() => {
    return currency ? createFormatPrice(lang, currency) : undefined;
  }, [lang, currency]);

  const { handleSubmit, register, errors, formState, watch, reset } = formMethods;
  const plan = watch("plan") as SmilePlusSubscriptionPlanProductEnum;
  const coupon = watch("coupon");
  const isNewsletter = watch("newsletter");
  const isTerms = watch("terms");
  const promoDetails = findSmilePlusPromoDetails(coupon);
  const planPrice = calculateSmilePlusPrice(plan, promoDetails);
  const isCardReady = cardReady.number && cardReady.expiration && cardReady.cvc;
  const isChosenPlanFree = planPrice === 0;
  const { isSubmitting } = formState;
  const canSubmit = isNewsletter && isTerms && (isChosenPlanFree ? true : isCardReady);

  const onSubmit = handleSubmit(async (values) => {
    const displayToastAndRedirect = () => {
      updateUserInfos({ newsletter: !!values.newsletter });

      try {
        GTagHelper.event({
          action: "FRONT | New SignUp | STEP 3 | User choosed plan",
          category: "SIGNUP",
          payload: { email: profile?.email },
        });
        if (typeof window.analytics !== "undefined")
          track("FRONT | New SignUp | STEP 3 | User choosed plan", {
            email: profile?.email,
          });
      } catch (e: any) {
        log(e, "error");
      }

      setHasJustRegistered();
      window.location.pathname = APP_SIGNUP_SUCCESS_ROUTE;

      if (!isChosenPlanFree) {
        sendSuccessToast();
      }
    };

    if (isChosenPlanFree) {
      const res = await smilePlusSubscribeMutation({
        variables: {
          plan: values.plan,
          coupon: values.coupon.toString().toLowerCase(),
        },
      });

      if (!res.data) {
        return;
      }

      displayToastAndRedirect();
    }

    // Handle card validation using stripe
    if (!stripe || !elements) {
      // Stripe.js has not loaded yet. Make sure to disable
      // form submission until Stripe.js has loaded.
      console.log("return");
      return;
    }

    const cardNumberElement = elements.getElement(CardNumberElement);

    if (!cardNumberElement) {
      return;
    }

    const payload = await stripe.createToken(cardNumberElement);

    if (!payload.token) {
      return;
    }

    const res = await smilePlusSubscribeMutation({
      variables: {
        plan: values.plan,
        cardToken: payload.token.id,
        coupon: values.coupon.toString().toLowerCase(),
      },
    });

    if (!res.data) {
      return;
    }

    const { threeDSecurePaymentIntentSecret } = res.data.smilePlusSubscribe;

    if (threeDSecurePaymentIntentSecret) {
      const { error: stripeError } = await stripe.confirmCardPayment(threeDSecurePaymentIntentSecret, {
        payment_method: {
          card: cardNumberElement,
        },
      });

      if (stripeError) {
        sendStripeErrorToast(stripeError.type);
        return;
      }
    }

    displayToastAndRedirect();
  });

  if (!plans || !formatPrice) {
    return (
      <Stack spacing="25px" mb="55px" mt={"55px"} h="100%">
        <Spinner size="xl" />
      </Stack>
    );
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={onSubmit}>
        <Stack spacing="25px" mb="55px" mt={"55px"} h="100%">
          <SmileSharedPlanRadioGroup plans={plans} defaultPlan={defaultPlan} formatPrice={formatPrice} />
          <Flex justify="space-between" align="center" w="100%">
            <Box>
              <FormInput name="coupon" label={t(`smile:smilePlusSignUp_promoLabel`)} defaultValue="" />
            </Box>
            {promoDetails && isSmilePlusValidPlanPromo(plan) && (
              <Box>
                <Text
                  fontFamily="Montserrat"
                  fontStyle="normal"
                  fontWeight="600"
                  fontSize="12px"
                  lineHeight="140%"
                  color="brandPink.200"
                >
                  {IS_HYBRIDGE && coupon.toLowerCase() === "levelup"
                    ? "12 months free"
                    : t(`smile:smilePlusSignUp_discountText`, {
                        discount: Math.round(promoDetails.discount),
                      })}
                </Text>
              </Box>
            )}
            {promoDetails && !isSmilePlusValidPlanPromo(plan) && (
              <Box>
                <Text
                  fontFamily="Montserrat"
                  fontStyle="normal"
                  fontWeight="600"
                  fontSize="12px"
                  lineHeight="140%"
                  color="tomato"
                >
                  {t(`smile:smilePlusSignUp_discountTextWrongPlan`)}
                </Text>
              </Box>
            )}
            <Box>
              <Text>
                {t(`smile:smilePlusSignUp_totalAmount`)}
                {formatPrice(calculateSmilePlusPrice(plan, promoDetails))}
              </Text>
            </Box>
          </Flex>
          {!isChosenPlanFree && (
            <>
              <PixMidTitle mt="25px">{t("smile:pixSignup_paymentMethodTitle")}</PixMidTitle>
              <FormLabel>
                <Text as="span" fontWeight="bold" fontSize="14px" lineHeight="20px" color="black">
                  {" "}
                  {t("smile:pixSignup_cardLabel")}
                </Text>

                <Box
                  border="1px solid #CCCCCC"
                  bg="#FFFFFF"
                  fontWeight="500"
                  fontSize="16px"
                  lineHeight="20px"
                  color="black"
                  borderRadius="4px"
                  mt="12px"
                  py="14px"
                  pl="20px"
                >
                  <CardNumberElement
                    onChange={(e) => {
                      if (e.complete) {
                        setCardReady((old) => ({ ...old, number: true }));
                      }
                    }}
                  />
                </Box>
              </FormLabel>
              <HStack spacing="30px">
                <FormLabel flex="0.5">
                  <Text as="span" fontWeight="bold" fontSize="14px" lineHeight="20px" color="black">
                    {t("smile:pixSignup_expirationLabel")}
                  </Text>
                  <Box
                    border="1px solid #CCCCCC"
                    bg="#FFFFFF"
                    fontWeight="500"
                    fontSize="16px"
                    lineHeight="20px"
                    color="black"
                    borderRadius="4px"
                    mt="12px"
                    py="14px"
                    pl="20px"
                  >
                    <CardExpiryElement
                      onChange={(e) => {
                        if (e.complete) {
                          setCardReady((old) => ({ ...old, expiration: true }));
                        }
                      }}
                    />
                  </Box>
                </FormLabel>
                <FormLabel flex="0.5">
                  <Text as="span" fontWeight="bold" fontSize="14px" lineHeight="20px" color="black">
                    {t("smile:pixSignup_cvvLabel")}
                  </Text>

                  <Box
                    border="1px solid #CCCCCC"
                    bg="#FFFFFF"
                    fontWeight="500"
                    fontSize="16px"
                    lineHeight="20px"
                    color="black"
                    borderRadius="4px"
                    py="14px"
                    mt="12px"
                    pl="20px"
                    flex="0.5"
                  >
                    <CardCvcElement
                      onChange={(e) => {
                        if (e.complete) {
                          setCardReady((old) => ({ ...old, cvc: true }));
                        }
                      }}
                    />
                  </Box>
                </FormLabel>
              </HStack>
            </>
          )}
          <Stack spacing="30px">
            <FormControl id="terms" isInvalid={!!errors.terms}>
              <Checkbox
                spacing="20px"
                fontFamily="Montserrat"
                fontStyle="normal"
                fontWeight="600"
                fontSize="9px"
                lineHeight="200%"
                color="#86868A"
                colorScheme="red"
                name="terms"
                ref={register}
                defaultChecked={isTerms}
                isRequired
              >
                {t("smile:smileSignup_termsOption")}
              </Checkbox>
            </FormControl>
            <FormControl id="newsletter" isInvalid={!!errors.newsletter}>
              <Checkbox
                spacing="20px"
                fontFamily="Montserrat"
                fontStyle="normal"
                fontWeight="600"
                fontSize="9px"
                lineHeight="200%"
                color="#86868A"
                colorScheme="red"
                name="newsletter"
                ref={register}
                defaultChecked={isNewsletter}
              >
                {IS_HYBRIDGE
                  ? t("smile:smileSignup_newsLetterOption").replace("Smile+", "Hybridge Smile Design")
                  : t("smile:smileSignup_newsLetterOption")}
              </Checkbox>
            </FormControl>
          </Stack>
          <Center>
            <Button type="submit" isLoading={isSubmitting} isDisabled={!canSubmit} isFullWidth={true}>
              {isChosenPlanFree ? t(`smile:smilePlusSignUp_payButtonFreeTrial`) : t(`smile:smilePlusSignUp_payButton`)}
              {isChosenPlanFree ? "" : formatPrice(planPrice)}
            </Button>
          </Center>
        </Stack>
      </form>
    </FormProvider>
  );
};

export default SmileSignupSubscriptionForm;
